import { Tooltip } from 'chart.js'

// Кастомный positioner для tooltip - всегда сверху бара
Tooltip.positioners.topOfBar = function (elements) {
	if (!elements.length) {
		return undefined;
	}

	const element = elements[0];
	const { x, y } = element.element;

	return {
		x: x,
		y: y,
		xAlign: 'center',
		yAlign: 'bottom'
	};
};

/**
 * Конвертировать HEX в RGBA
 */
export function hexToRgba(hex, alpha) {
	if (!hex || hex.charAt(0) !== '#' || hex.length < 7) return `rgba(0, 0, 0, ${alpha})`;
	const r = parseInt(hex.slice(1, 3), 16);
	const g = parseInt(hex.slice(3, 5), 16);
	const b = parseInt(hex.slice(5, 7), 16);
	return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

/**
 * KPI Bar chart options (payments_made, conversion placeholder)
 */
export function createKpiBarOptions(handleKpiTooltip) {
	return {
		responsive: true,
		maintainAspectRatio: false,
		plugins: {
			legend: { display: false },
			tooltip: {
				enabled: false,
				mode: 'index',
				intersect: false,
				external: handleKpiTooltip
			}
		},
		scales: {
			x: { display: false, grid: { display: false }, border: { display: false } },
			y: { display: false, grid: { display: false }, border: { display: false } }
		}
	};
}

/**
 * KPI Line chart options (total_revenue, refunds)
 */
export function createKpiLineOptions(handleKpiTooltip) {
	return {
		responsive: true,
		maintainAspectRatio: false,
		plugins: {
			legend: { display: false },
			tooltip: {
				enabled: false,
				mode: 'index',
				intersect: false,
				external: handleKpiTooltip
			}
		},
		scales: {
			x: { display: false, grid: { display: false }, border: { display: false } },
			y: { display: false, grid: { display: false }, border: { display: false } }
		},
		elements: {
			point: { radius: 0, hoverRadius: 4 },
			line: { tension: 0.35 }
		}
	};
}

/**
 * Conversion bar chart options (3D bars)
 */
export function createConversionBarOptions(handleConversionTooltip) {
	return {
		responsive: true,
		maintainAspectRatio: false,
		plugins: {
			legend: { display: false },
			tooltip: {
				enabled: false,
				mode: 'index',
				intersect: true,
				external: handleConversionTooltip
			}
		},
		scales: {
			x: { display: false, grid: { display: false }, border: { display: false } },
			y: { display: false, grid: { display: false }, border: { display: false }, beginAtZero: true }
		},
		layout: {
			padding: { top: 0, right: 20 }
		}
	};
}

/**
 * Revenue Trends line chart options
 */
export function createTrendsLineOptions(handleExternalTooltip, colors) {
	return {
		responsive: true,
		maintainAspectRatio: false,
		plugins: {
			legend: { display: false },
			tooltip: {
				enabled: false,
				mode: 'index',
				intersect: false,
				external: handleExternalTooltip
			}
		},
		scales: {
			x: {
				border: { display: false },
				grid: { display: false, drawBorder: false },
				ticks: {
					color: colors.grey,
					font: { size: 12 },
					maxRotation: 0,
					minRotation: 0,
					autoSkip: false,
					callback: function (value, index, ticks) {
						if (index === 0 || index === ticks.length - 1) {
							return this.getLabelForValue(value);
						}
						return '';
					}
				}
			},
			y: {
				border: { display: false },
				grid: { display: true, drawBorder: false, color: colors.grid },
				ticks: {
					color: colors.grey,
					font: { size: 12 }
				}
			}
		},
		elements: {
			point: { radius: 0 },
			line: { tension: 0.35 }
		}
	};
}

/**
 * 3D effect plugin for conversion bars
 * Draws a flat right-side panel to simulate depth
 */
export function createBar3DPlugin(colors) {
	return {
		id: 'bar3D',
		afterDatasetsDraw(chart) {
			const { ctx } = chart;
			const meta = chart.getDatasetMeta(0);
			const dataset = chart.data.datasets[0];

			if (!meta || !meta.data) return;

			meta.data.forEach((bar, index) => {
				const { x, y, width, height, base } = bar;
				if (height <= 0 || !width) return;

				const bgColor = dataset.backgroundColor[index];
				const isPlaceholder = bgColor === colors.placeholder;
				if (isPlaceholder) return;

				const depth = width * 0.3;
				const halfWidth = width / 2;

				ctx.save();

				// Right face (flat rectangle)
				ctx.beginPath();
				ctx.rect(x + halfWidth, y, depth, base - y);
				ctx.fillStyle = hexToRgba(colors.blue, 0.25);
				ctx.fill();

				ctx.restore();
			});
		}
	};
}

/**
 * Empty chart data for initialization
 */
export function createEmptyChartData(color) {
	return {
		labels: [],
		datasets: [{
			data: [],
			backgroundColor: color,
			borderColor: color
		}]
	};
}

/**
 * Convert API data to Chart.js format
 */
export function apiDataToChartData(apiData, color, chartType, formatDateLabel, gradientOpacity = 0.25) {
	if (!apiData || apiData.length === 0) {
		return createEmptyChartData(color);
	}

	const labels = apiData.map(d => formatDateLabel(d.bucket_start || d.date));
	const values = apiData.map(d => d.value);

	if (chartType === 'bar') {
		return {
			labels,
			datasets: [{
				data: values,
				backgroundColor: color,
				borderColor: color,
				borderRadius: 2,
				barThickness: 'flex'
			}]
		};
	} else {
		return {
			labels,
			datasets: [{
				data: values,
				fill: true,
				backgroundColor: (context) => {
					const ctx = context.chart.ctx;
					const gradient = ctx.createLinearGradient(0, 0, 0, 100);
					gradient.addColorStop(0, hexToRgba(color, gradientOpacity));
					gradient.addColorStop(1, hexToRgba(color, 0));
					return gradient;
				},
				borderColor: color,
				borderWidth: 2,
				pointRadius: 0,
				pointHoverRadius: 0
			}]
		};
	}
}

/**
 * Create conversion chart data with 3D bars
 */
export function createConversionChartData(total, processed, paid, colors) {
	const hasTotalData = total > 0;
	const hasProcessedData = processed > 0;
	const hasPaidData = paid > 0;
	const displayTotal = hasTotalData ? total : 1;
	const displayProcessed = hasProcessedData ? processed : 1;
	const displayPaid = hasPaidData ? paid : 1;

	return {
		labels: ['All', 'Processed', 'Successful'],
		// Реальные значения для тултипа
		realValues: [total, processed, paid],
		datasets: [{
			data: [displayTotal, displayProcessed, displayPaid],
			backgroundColor: [
				hasTotalData ? colors.blue : colors.placeholder,
				hasProcessedData ? colors.blue : colors.placeholder,
				hasPaidData ? colors.blue : colors.placeholder
			],
			borderRadius: 0,
			borderSkipped: false,
			barPercentage: 0.85,
			categoryPercentage: 0.9
		}]
	};
}

/**
 * Get trend line colors array
 */
export function getTrendsColors(colors) {
	return [colors.green, colors.blue, colors.orange, colors.violet, colors.orangeWarm, colors.blueGrey, colors.brown];
}

/**
 * Get payment method icon colors array
 */
export function getMethodColors(colors) {
	return [colors.violet, colors.blue, colors.orange, colors.orangeWarm, colors.blueGrey];
}
